import React, { useState, useMemo } from 'react';
import { UserProfile } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { MarcomAppNavBarMobileProps, HeaderLinksType } from '../../AppNavBar.types';

export enum MobileMenuType {
  mainMenu = 'mainMenu',
  subMenu = 'subMenu',
  blogMenu = 'blogMenu',
}

type MobileViewContextProps = MarcomAppNavBarMobileProps & {
  currentUser: UserProfile;
  eventId: Maybe<string>;
  eventHandle: Maybe<string>;
  isMobileNavOpen: boolean;
  selectedMenu: MobileMenuType;
  selectedMenuType?: HeaderLinksType;
  selectedMenuIndex: number;
  onSwitchEventClick: () => void;
  setMobileNavIsOpen: (isOpen: boolean) => void;
  onClickMainMenu: (type: HeaderLinksType) => void;
  onClickSubMenu: (index: number, type?: HeaderLinksType) => void;
  onClickBack: (shoulGoToMain?: boolean) => void;
};

export const MobileViewContext = React.createContext<MobileViewContextProps>({
  currentUser: {} as UserProfile,
  isMobileNavOpen: false,
  eventId: null,
  eventHandle: null,
  selectedMenu: MobileMenuType.mainMenu,
  selectedMenuType: undefined,
  selectedMenuIndex: -1,
  setMobileNavIsOpen: () => {
    throw Error('No MobileView Provider');
  },
  onSwitchEventClick: () => {
    throw Error('No MobileView Provider');
  },
  onClickMainMenu: (type: HeaderLinksType) => {
    throw Error('No MobileView Provider');
  },
  onClickSubMenu: (index: number, type?: HeaderLinksType) => {
    throw Error('No MobileView Provider');
  },
  onClickBack: (shoulGoToMain?: boolean) => {
    throw Error('No MobileView Provider');
  },
});

export function useMobileViewContext() {
  const context = React.useContext(MobileViewContext);
  if (!context) {
    throw new Error('useMobileViewContext() can only be used within a MobileViewProvider.');
  }
  return context;
}

export const MobileViewProvider: React.FC<MarcomAppNavBarMobileProps> = ({
  currentUser,
  isMobileNavOpen,
  eventId,
  eventHandle,
  onSwitchEventClick,
  setMobileNavIsOpen,
  children,
}) => {
  const [selectedMenu, setSelectedMenu] = useState(MobileMenuType.mainMenu);
  const [selectedMenuType, setSelectedMenuType] = useState<HeaderLinksType | undefined>(undefined);
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(-1);

  const onClickMainMenu = useEventCallback((type: HeaderLinksType) => {
    setSelectedMenu(MobileMenuType.subMenu);
    setSelectedMenuType(type);
  });

  const onClickSubMenu = useEventCallback((index: number, type?: HeaderLinksType) => {
    if (type) {
      setSelectedMenuType(type);
    }
    setSelectedMenu(MobileMenuType.blogMenu);
    setSelectedMenuIndex(index);
  });

  const onClickBack = useEventCallback((shoulGoToMain?: boolean) => {
    if (!currentUser.profile || shoulGoToMain) {
      setSelectedMenu(MobileMenuType.mainMenu);
      setSelectedMenuType(undefined);
      setSelectedMenuIndex(-1);
    } else {
      setSelectedMenu(MobileMenuType.subMenu);
      setSelectedMenuIndex(-1);
    }
  });

  const value: MobileViewContextProps = useMemo((): MobileViewContextProps => {
    return {
      currentUser,
      isMobileNavOpen,
      eventId,
      eventHandle,
      selectedMenu,
      selectedMenuType,
      selectedMenuIndex,
      setMobileNavIsOpen,
      onSwitchEventClick,
      onClickMainMenu,
      onClickSubMenu,
      onClickBack,
    };
  }, [
    currentUser,
    isMobileNavOpen,
    eventId,
    eventHandle,
    selectedMenu,
    selectedMenuType,
    selectedMenuIndex,
    setMobileNavIsOpen,
    onSwitchEventClick,
    onClickMainMenu,
    onClickSubMenu,
    onClickBack,
  ]);

  return <MobileViewContext.Provider value={value}>{children}</MobileViewContext.Provider>;
};
